import React, { useEffect, useMemo, useCallback, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import NiceModal from "@ebay/nice-modal-react";
import { ProductImageModal } from "components/_Modals/ProductImageModal";
import { getHomepage } from "utils";
import {
  useFetchScrivitoImages,
  useTranslate,
  useProductConfiguratorSliderImageScroll,
} from "hooks";
import { Select } from "components/Select/Select";
import s from "../ProductConfiguratorComponent.module.scss";
import { IconButton } from "components/IconButton/IconButton";

export const ImageDropdownWithSlider = Scrivito.connect(
  ({ images = [], button3dView, isDisabled = false }) => {
    const translate = useTranslate();
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [hoveredImageIndex, setHoveredImageIndex] = useState(null);
    const scrivitoImages = useFetchScrivitoImages(images);

    const { scrollContainerRef, imageRefs, handleWheel } =
      useProductConfiguratorSliderImageScroll(selectedImageIndex);

    const handleSelectImage = (e) => {
      setSelectedImageIndex(e.index);
    };

    useEffect(() => {
      setSelectedImageIndex(0);
    }, [images]);

    const productListPage = getHomepage()?.get("productListPage");
    const defaultImage = productListPage?.get("defaultProductImage");

    if (!scrivitoImages) {
      return null;
    }

    const currentImage = useMemo(
      () => scrivitoImages[selectedImageIndex] || defaultImage,
      [defaultImage, scrivitoImages, selectedImageIndex],
    );

    const currentImageUrl = useScrivitoImageUrl(currentImage);

    const options = useMemo(
      () =>
        images &&
        images.map((image, index) => ({
          label: image.text,
          value: image.pimId + index,
          index,
        })),
      [images],
    );

    const selectedOption = options[selectedImageIndex];

    const handleImageClick = (index) => {
      if (index !== selectedImageIndex) {
        setSelectedImageIndex(index);
      }
    };

    const onWheel = useCallback(
      (e) => handleWheel(e, scrivitoImages.length, setSelectedImageIndex),
      [handleWheel, scrivitoImages.length],
    );

    return (
      <div
        className={cx("m-0 h-100 w-100 d-flex flex-column", s.cardContainer)}
      >
        <div className="pb-2 d-flex gap-2 align-items-start">
          <div className="flex-grow-1">
            <Select
              options={options}
              onChange={handleSelectImage}
              value={selectedOption}
              name="imageSelect"
              testId="image-select"
              className="font-md"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isDisabled={isDisabled}
            />
          </div>
          {button3dView ? (
            <div className={cx("flex-shrink-0", s.topButton)}>
              {button3dView}
            </div>
          ) : (
            <div>Button will be here</div>
          )}
        </div>
        <div
          ref={scrollContainerRef}
          className={s.layoutMediaImgContainer}
          onWheel={onWheel}
        >
          {scrivitoImages.map((image, index) => {
            const isActive = index === selectedImageIndex;
            const isHovered = index === hoveredImageIndex;

            return (
              <div
                key={index}
                ref={(el) => {
                  imageRefs.current[index] = el;
                }}
                className={cx(
                  s.layoutMediaImgContainerImgCard,
                  "position-relative p-3 rounded-2 cursor-pointer",
                  {
                    [s.imageCardBorder]: isActive && !isDisabled,
                    [s.imageCardBorderDisabled]: isActive && isDisabled,
                    "shadow-sm": !isHovered && !isDisabled,
                    [s.imageCardShadow]: isHovered && !isDisabled,
                  },
                )}
                onClick={() => handleImageClick(index)}
                onMouseEnter={() => setHoveredImageIndex(index)}
                onMouseLeave={() => setHoveredImageIndex(null)}
              >
                <div
                  className={cx(s.imageCard, "position-relative h-100 w-100")}
                >
                  <Scrivito.ImageTag
                    className={cx(
                      "img-fluid w-100 h-100",
                      {
                        "opacity-25": !isActive,
                      },
                      s.imageCardImage,
                    )}
                    content={image || defaultImage}
                  />

                  <div className={s.imageCardImageLabel}>
                    {images[index]?.text}
                  </div>
                </div>

                {isActive && (
                  <div className={s.imageCardIconsContainer}>
                    <IconButton
                      testId="media-header-arrows-maximize-icon"
                      id="media-header-arrows-maximize-icon"
                      iconPrefix="trox"
                      icon="arrows-maximize-regular"
                      color="tertiary"
                      iconClassName={cx("icon-14 p-0")}
                      className="border-0"
                      tooltipText={translate("MAXIMIZE")}
                      disabled={isDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        NiceModal.show(ProductImageModal, {
                          image: currentImage,
                        });
                      }}
                    />
                    <IconButton
                      testId="media-img-arrow-up-right-square-regular-icon"
                      id="media-img-arrow-up-right-square-regular-icon"
                      iconPrefix="trox"
                      icon="arrow-up-right-square-regular"
                      color="tertiary"
                      iconClassName={cx("icon-14 p-0")}
                      className="border-0"
                      tooltipText={translate(
                        "PRODUCT_CONFIGURATOR_MEDIA_VIEW_IN_A_NEW_TAB",
                      )}
                      disabled={isDisabled}
                      onClick={() =>
                        currentImageUrl &&
                        window.open(currentImageUrl, "_blank")
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

const useScrivitoImageUrl = (image) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (image) {
      if (!image?.contentUrl) {
        return;
      }
      Scrivito.load(() => image?.contentUrl()).then((url) => {
        setImageUrl(url);
      });
    }
  }, [image]);

  return imageUrl;
};
